// Chronal Eddy — custom timed event (custom-events-v1 §E4). A time rift
// (~14 u radius) opens at a seeded position: inside, the player moves and
// fires at 1.4× while enemies crawl at 0.5×. Outside, everything is normal —
// the gamble is positional, the rift sits where it sits.
//
// Mechanics (spec) — with one deliberate implementation deviation:
//   The spec asks for a galaxy-zone-style duck-typed `haste` write on the
//   player. That channel is closed to events: game.ts rewrites `player.haste`
//   every tick BEFORE updatePlayer/updateWeapons read it, and the events
//   manager runs AFTER both in the sim step, so an event-side `haste` write
//   would always be stomped before any reader saw it. Instead the haste is
//   delivered by two event-local, deterministic post-adjusts:
//   - Move 1.4×: displacement post-adjust (see post-adjust.ts) — the rift
//     amplifies whatever movement integrated this tick, dash included.
//   - Fire 1.4×: every tick inside the rift, each weapon's cooldown_until
//     is shifted 0.4 ticks earlier, so a cooldown that takes C ticks instead
//     completes in C/1.4 (shift accumulates while inside; already-banked
//     shift stays when the player steps out — time really did pass faster).
//   - Enemies 0.5×: displacement post-adjust, gated to the rift radius.
//
// Determinism (GDD Appendix A §A.1): the rift position comes from a private
// mulberry32 stream reseeded from world.runSeed in start(); everything else
// is a pure function of world state and tick. No Math.random, no wall-clock.
//
// Perf: one pass over the enemy pool per tick; tracker entries are reused,
// so steady-state update() allocates nothing.
//
// Visuals (wired by the parent, not here): 'phase_change'
//   { system: 'events', event_id: 'chronal_eddy', phase: 'start'|'end', danger: 1, pos, radius, duration }
// Suggested look: shimmering clock-face bubble; sped-up trails inside.

import type { Vec2 } from '../../types.js';
import type { WorldState } from '../state.js';
import type { GameEventDef } from '../events-manager.js';
import { RngStream } from '../rng.js';
import { wrappedDelta } from '../collision.js';
import { DisplacementScaler, PlayerDisplacementScaler } from './post-adjust.js';

/** Event lifetime: 12 s at 60 Hz. */
const EVENT_DURATION_TICKS = 12 * 60;
/** Rift radius (arena units). */
const RIFT_RADIUS = 14;
/** Player haste inside the rift: move/fire at 1.4×. */
const HASTE_FACTOR = 1.4;
/** Per-tick weapon-cooldown shift while inside: HASTE_FACTOR − 1. */
const COOLDOWN_SHIFT_PER_TICK = HASTE_FACTOR - 1;
/** Enemy crawl inside the rift. */
const ENEMY_FACTOR = 0.5;
/** Placement margin kept inside the ±100 arena walls. */
const ARENA_MARGIN = 10;
/** Private stream salt: keeps rift placement independent of every other system. */
const EDDY_STREAM_SALT = 0xC120A1ED;

/**
 * Create the Chronal Eddy event def. All mutable state lives in this closure
 * and is fully reset in start(), so the def instance is reusable across runs
 * and across multiple triggers within one run.
 */
export function createChronalEddyEvent(): GameEventDef {
  const center: Vec2 = { x: 0, y: 0 };
  let stream = new RngStream(0);
  const slow = new DisplacementScaler();
  const haste = new PlayerDisplacementScaler();

  function insideRift(p: Vec2): boolean {
    const d = wrappedDelta(p, center);
    return d.x * d.x + d.y * d.y <= RIFT_RADIUS * RIFT_RADIUS;
  }

  return {
    id: 'chronal_eddy',
    name: 'Chronal Eddy',
    duration: EVENT_DURATION_TICKS,

    start(world: WorldState, tick: number): void {
      stream = new RngStream((world.runSeed ^ EDDY_STREAM_SALT) >>> 0);
      const half = 100 - ARENA_MARGIN;
      center.x = stream.next_range(-half, half);
      center.y = stream.next_range(-half, half);
      slow.reset();
      haste.reset();
      world.events.emit(
        'phase_change',
        {
          system: 'events',
          event_id: 'chronal_eddy',
          phase: 'start',
          danger: 1,
          pos: { x: center.x, y: center.y },
          radius: RIFT_RADIUS,
          duration: EVENT_DURATION_TICKS
        },
        tick
      );
    },

    update(world: WorldState, _tick: number, _progress: number): void {
      // Enemies crawl inside the rift.
      slow.apply(world.enemies.alive(), ENEMY_FACTOR, (e) => insideRift(e.pos));

      const player = world.player;
      if (!player.alive) {
        haste.reset();
        return;
      }
      if (insideRift(player.pos)) {
        // Fire haste: bring every weapon cooldown 0.4 ticks closer. Runs
        // after updateWeapons in the sim step, so a cooldown that expired
        // this tick was already consumed — only future waits are shortened.
        for (const w of player.weapons) {
          w.cooldown_until -= COOLDOWN_SHIFT_PER_TICK;
        }
      }
      // Move haste: amplify this tick's displacement — but only when the
      // whole move happened inside the rift (a wormhole teleport INTO the
      // rift must not get its jump amplified).
      haste.apply(player, HASTE_FACTOR, (prev, cur) => insideRift(prev) && insideRift(cur));
    },

    end(world: WorldState, tick: number): void {
      slow.reset();
      haste.reset();
      world.events.emit(
        'phase_change',
        {
          system: 'events',
          event_id: 'chronal_eddy',
          phase: 'end',
          danger: 1,
          pos: { x: center.x, y: center.y },
          radius: RIFT_RADIUS
        },
        tick
      );
    }
  };
}
